import AbstractApiModel from '@/models/abstracts/AbstractApiModel';
import doc from '@model-core/decorators/doc';
import type { Dayjs } from 'dayjs';

import { APP_ROLE, USER_STATUS } from '@/models/references/references';
import { referenceOf } from '@/stores/useReferenceOptions';
import { urnOf } from '@/models/urn';

/** URN pour écrire une relation vers cette ressource (corps d'un POST / PATCH) ; un filtre prend l'id */
export const userUrn = (userId: id | number) => urnOf('user', userId);

/**
 * Utilisateur (GET / POST / PATCH /users). appRole et userStatus arrivent en objets {id, reference, label} (enums de
 * l'API) : le front s'appuie sur la reference. Écriture : email, firstName, lastName, appRole (IRI), plainPassword (admin).
 */
export default class UserModel extends AbstractApiModel {
  protected _path = '/users/{?id}';

  declare _tsFilters: { email?: string; appRole?: id; userStatus?: id };

  @doc.string declare email: string;
  @doc.string declare firstName: string;
  @doc.string declare lastName: string;
  @doc.boolean declare passwordDefined: boolean;
  @doc.date declare createdAt: Dayjs;
  /** Auteur de la création : URN de la personne connectée ($:teiji:user:12), vide pour les fixtures, migrations, imports */
  @doc.string declare createdBy: string;
  @doc.date declare updatedAt: Dayjs;
  /** Auteur de la dernière modification (URN) */
  @doc.string declare updatedBy: string;

  /** NOM Prénom : convention du projet, le nom toujours avant le prénom */
  get fullName(): string {
    return `${this.lastName} ${this.firstName}`.trim();
  }

  /** ADMIN / USER */
  get appRoleReference(): string {
    return referenceOf('appRoles', this.get<unknown, unknown>('appRole', null))?.reference ?? '';
  }

  get appRoleLabel(): string {
    return referenceOf('appRoles', this.get<unknown, unknown>('appRole', null))?.label ?? '';
  }

  /** Rôles du front (ROLE_ADMIN / ROLE_USER, comme AppRole::getSecurityRole de l'API), pour le menu et les accès */
  get securityRoles(): string[] {
    // Rôle inconnu (liste des rôles pas chargée) : le moindre privilège
    return [`ROLE_${this.appRoleReference || APP_ROLE.USER}`];
  }

  get isEnabled(): boolean {
    return (
      referenceOf('userStatuses', this.get<unknown, unknown>('userStatus', null))?.reference === USER_STATUS.ENABLED
    );
  }
}
